/**
 * AccountSettingsPage - User Account Settings & Profile Management
 * A proper settings page accessible from the top-right menu
 * Includes: Profile info, Password change, Email preferences, etc.
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  Avatar,
  Divider,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Switch,
  InputAdornment,
  IconButton,
  Chip,
  Paper,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel
} from '@mui/material';
import PhoneField from '../components/common/PhoneField';
import {
  Person,
  Lock,
  Email,
  Notifications,
  Security,
  Phone,
  LocationOn,
  Visibility,
  VisibilityOff,
  Save,
  Edit,
  Check,
  Warning,
  PhotoCamera,
  Business
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { brand } from '../theme/colors';

const AccountSettingsPage = () => {
  const { user, isAuthenticated } = useAuth();
  const [activeSection, setActiveSection] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    address: '',
    bio: '',
    primaryTrade: '',
    accountType: 'individual',
    companyName: '',
    registrationNumber: '',
    // Contractor-specific
    isAvailable: true,
    minJobFee: '',
    maxTravelDistance: ''
  });
  const [profileEditing, setProfileEditing] = useState(false);
  
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  
  // Notification preferences state
  const [notifications, setNotifications] = useState({
    emailBids: true,
    emailMessages: true,
    emailProjects: true,
    emailMarketing: false,
    pushNotifications: true,
    smsAlerts: false
  });

  // Fetch user profile data
  useEffect(() => {
    const fetchProfile = async () => {
      if (!isAuthenticated || !user) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/auth/me');

        const userData = response.data.data.user;
        setProfileData(userData);
        const p = userData.profile || {};

        setProfileForm({
          firstName: p.firstName || p.first_name || userData.firstName || '',
          lastName: p.lastName || p.last_name || userData.lastName || '',
          email: userData.email || '',
          phone: p.phoneNumber || p.phone_number || userData.phone || '',
          city: p.city || userData.city || '',
          address: p.address || userData.address || '',
          bio: p.bio || '',
          primaryTrade: p.primaryTrade || p.primary_trade || '',
          accountType: p.accountType || 'individual',
          companyName: p.companyName || '',
          registrationNumber: p.registrationNumber || '',
          // Contractor-specific
          isAvailable: p.is_available !== false,
          minJobFee: p.min_job_fee || '',
          maxTravelDistance: p.max_travel_distance || ''
        });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching profile:', err);
        showSnackbar('Failed to load profile data', 'error');
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user, isAuthenticated]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Save profile changes
  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/auth/profile', {
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
        phone: profileForm.phone,
        city: profileForm.city,
        address: profileForm.address,
        bio: profileForm.bio,
        accountType: profileForm.accountType,
        companyName: profileForm.companyName,
        registrationNumber: profileForm.registrationNumber
      });

      // Save contractor-specific availability fields
      if (user?.role === 'contractor') {
        await api.put('/contractors/profile/me', {
          isAvailable: profileForm.isAvailable,
          minJobFee: profileForm.minJobFee ? parseFloat(profileForm.minJobFee) : null,
          maxTravelDistance: profileForm.maxTravelDistance ? parseInt(profileForm.maxTravelDistance) : null
        });
      }

      setProfileEditing(false);
      showSnackbar('Profile updated successfully');
    } catch (err) {
      console.error('Error saving profile:', err);
      showSnackbar(err.response?.data?.message || 'Failed to update profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Validate and change password
  const handleChangePassword = async () => {
    // Validate
    const errors = {};
    if (!passwordForm.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }
    if (!passwordForm.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordForm.newPassword)) {
      errors.newPassword = 'Password must contain uppercase, lowercase, and number';
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    setSaving(true);
    setPasswordErrors({});
    
    try {
      await api.put('/auth/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });

      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      showSnackbar('Password changed successfully');
    } catch (err) {
      console.error('Error changing password:', err);
      if (err.response?.status === 401) {
        setPasswordErrors({ currentPassword: 'Current password is incorrect' });
      } else {
        showSnackbar(err.response?.data?.message || 'Failed to change password', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  // Save notification preferences
  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      await api.put('/auth/notification-preferences', notifications);

      showSnackbar('Notification preferences saved');
    } catch (err) {
      console.error('Error saving notifications:', err);
      showSnackbar('Failed to save preferences', 'error');
    } finally {
      setSaving(false);
    }
  };

  const menuItems = [
    { id: 'profile', label: 'Profile Information', icon: <Person /> },
    { id: 'password', label: 'Change Password', icon: <Lock /> },
    { id: 'notifications', label: 'Notification Preferences', icon: <Notifications /> },
    { id: 'security', label: 'Security', icon: <Security /> }
  ];

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">Please log in to access account settings.</Alert>
      </Container>
    );
  }

  const renderProfileSection = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" fontWeight={600}>Profile Information</Typography>
          {!profileEditing ? (
            <Button startIcon={<Edit />} onClick={() => setProfileEditing(true)}>
              Edit
            </Button>
          ) : (
            <Box>
              <Button onClick={() => setProfileEditing(false)} sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button 
                variant="contained" 
                startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                onClick={handleSaveProfile}
                disabled={saving}
              >
                Save
              </Button>
            </Box>
          )}
        </Box>

        {/* Avatar section */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Avatar
            sx={{ 
              width: 100, 
              height: 100, 
              bgcolor: brand.primary,
              fontSize: '2rem'
            }}
          >
            {profileForm.accountType === 'company' && profileForm.companyName 
              ? profileForm.companyName.charAt(0) 
              : `${profileForm.firstName?.charAt(0) || ''}${profileForm.lastName?.charAt(0) || ''}`}
          </Avatar>
          <Box sx={{ ml: 3 }}>
            {profileForm.accountType === 'company' && profileForm.companyName && (
              <Typography variant="h5" fontWeight={600}>
                {profileForm.companyName}
              </Typography>
            )}
            <Typography variant={profileForm.accountType === 'company' ? 'body1' : 'h5'} fontWeight={profileForm.accountType === 'company' ? 400 : 600} color={profileForm.accountType === 'company' ? 'text.secondary' : 'text.primary'}>
              {profileForm.firstName} {profileForm.lastName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
              <Chip 
                label={user.role?.replace('_', ' ').toUpperCase()} 
                size="small" 
                sx={{ bgcolor: brand.primary, color: 'white' }}
              />
              {profileForm.accountType === 'company' && (
                <Chip
                  icon={<Business sx={{ color: 'white !important', fontSize: 16 }} />}
                  label="COMPANY"
                  size="small"
                  sx={{ bgcolor: '#f57c00', color: 'white' }}
                />
              )}
              {user.email_verified && (
                <Chip 
                  icon={<Check sx={{ color: 'white !important' }} />}
                  label="Verified" 
                  size="small" 
                  sx={{ bgcolor: '#4caf50', color: 'white' }}
                />
              )}
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="First Name"
              value={profileForm.firstName}
              onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
              disabled={!profileEditing}
              InputProps={{
                startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={profileForm.lastName}
              onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
              disabled={!profileEditing}
              InputProps={{
                startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email"
              value={profileForm.email}
              disabled
              helperText="Email cannot be changed"
              InputProps={{
                startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PhoneField
              label="Phone Number"
              value={profileForm.phone}
              onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
              disabled={!profileEditing}
              helperText={profileEditing ? 'With the country code, so we can reach you on WhatsApp' : undefined}
              InputProps={{
                startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="City"
              value={profileForm.city}
              onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
              disabled={!profileEditing}
              InputProps={{
                startAdornment: <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Address"
              value={profileForm.address}
              onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
              disabled={!profileEditing}
              InputProps={{
                startAdornment: <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>

          {/* Bio field for contractors, verifiers, merchants */}
          {['contractor', 'verifier', 'merchant'].includes(user?.role) && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bio / About Me"
                value={profileForm.bio}
                onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                disabled={!profileEditing}
                multiline
                rows={3}
                placeholder="Tell clients about your experience, specialties, and what makes you stand out..."
                helperText={`${profileForm.bio?.length || 0}/500 characters`}
                inputProps={{ maxLength: 500 }}
              />
            </Grid>
          )}

          {/* Contractor availability fields */}
          {user?.role === 'contractor' && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Availability & Work Preferences
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={profileForm.isAvailable}
                      onChange={(e) => setProfileForm({ ...profileForm, isAvailable: e.target.checked })}
                      disabled={!profileEditing}
                    />
                  }
                  label="Currently accepting new work"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Minimum Job Fee (USD)"
                  type="number"
                  value={profileForm.minJobFee}
                  onChange={(e) => setProfileForm({ ...profileForm, minJobFee: e.target.value })}
                  disabled={!profileEditing}
                  placeholder="e.g. 500"
                  helperText="Minimum amount you accept for a job"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Max Travel Distance (km)"
                  type="number"
                  value={profileForm.maxTravelDistance}
                  onChange={(e) => setProfileForm({ ...profileForm, maxTravelDistance: e.target.value })}
                  disabled={!profileEditing}
                  placeholder="e.g. 100"
                  helperText="How far you'll travel from your base city"
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </>
          )}

          {/* Account Type — shown for contractors, verifiers, merchants */}
          {['contractor', 'verifier', 'merchant'].includes(user?.role) && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <FormControl component="fieldset" disabled={!profileEditing} sx={{ mt: 1 }}>
                  <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                    Account Type
                  </FormLabel>
                  <RadioGroup
                    row
                    value={profileForm.accountType}
                    onChange={(e) => setProfileForm({ ...profileForm, accountType: e.target.value })}
                  >
                    <FormControlLabel 
                      value="individual" 
                      control={<Radio />} 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Person fontSize="small" /> Individual
                        </Box>
                      }
                    />
                    <FormControlLabel 
                      value="company" 
                      control={<Radio />} 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Business fontSize="small" /> Company
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {profileForm.accountType === 'company' && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Company Name"
                      value={profileForm.companyName}
                      onChange={(e) => setProfileForm({ ...profileForm, companyName: e.target.value })}
                      disabled={!profileEditing}
                      InputProps={{
                        startAdornment: <Business sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Registration Number"
                      value={profileForm.registrationNumber}
                      onChange={(e) => setProfileForm({ ...profileForm, registrationNumber: e.target.value })}
                      disabled={!profileEditing}
                      placeholder="e.g. CR/2024/12345"
                      helperText="Company registration or tax number"
                    />
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      </CardContent>
    </Card>
  );

  const renderPasswordSection = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
          Change Password
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Choose a strong password with at least 8 characters, including uppercase, lowercase, and numbers.
        </Alert>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Current Password"
              type={showPasswords.current ? 'text' : 'password'}
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              error={!!passwordErrors.currentPassword}
              helperText={passwordErrors.currentPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}>
                      {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="New Password"
              type={showPasswords.new ? 'text' : 'password'}
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              error={!!passwordErrors.newPassword}
              helperText={passwordErrors.newPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}>
                      {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Confirm New Password"
              type={showPasswords.confirm ? 'text' : 'password'}
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              error={!!passwordErrors.confirmPassword}
              helperText={passwordErrors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}>
                      {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Button 
              variant="contained" 
              onClick={handleChangePassword}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : <Lock />}
              sx={{ bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark } }}
            >
              Change Password
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderNotificationsSection = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
          Notification Preferences
        </Typography>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          Email Notifications
        </Typography>
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <List disablePadding>
            <ListItem divider>
              <ListItemIcon><Email /></ListItemIcon>
              <ListItemText 
                primary="Bid Updates" 
                secondary="Get notified when you receive new bids or bid status changes"
              />
              <Switch
                checked={notifications.emailBids}
                onChange={(e) => setNotifications({ ...notifications, emailBids: e.target.checked })}
              />
            </ListItem>
            <ListItem divider>
              <ListItemIcon><Email /></ListItemIcon>
              <ListItemText 
                primary="Messages" 
                secondary="Email notifications for new messages"
              />
              <Switch
                checked={notifications.emailMessages}
                onChange={(e) => setNotifications({ ...notifications, emailMessages: e.target.checked })}
              />
            </ListItem>
            <ListItem divider>
              <ListItemIcon><Email /></ListItemIcon>
              <ListItemText 
                primary="Project Updates" 
                secondary="Updates about your projects, milestones, and verifications"
              />
              <Switch
                checked={notifications.emailProjects}
                onChange={(e) => setNotifications({ ...notifications, emailProjects: e.target.checked })}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Email /></ListItemIcon>
              <ListItemText 
                primary="Marketing & Tips" 
                secondary="Occasional tips and platform updates"
              />
              <Switch
                checked={notifications.emailMarketing}
                onChange={(e) => setNotifications({ ...notifications, emailMarketing: e.target.checked })}
              />
            </ListItem>
          </List>
        </Paper>

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          Other Notifications
        </Typography>
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <List disablePadding>
            <ListItem divider>
              <ListItemIcon><Notifications /></ListItemIcon>
              <ListItemText 
                primary="Push Notifications" 
                secondary="Browser push notifications for important updates"
              />
              <Switch
                checked={notifications.pushNotifications}
                onChange={(e) => setNotifications({ ...notifications, pushNotifications: e.target.checked })}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Phone /></ListItemIcon>
              <ListItemText 
                primary="SMS Alerts" 
                secondary="Text message alerts for critical updates"
              />
              <Switch
                checked={notifications.smsAlerts}
                onChange={(e) => setNotifications({ ...notifications, smsAlerts: e.target.checked })}
              />
            </ListItem>
          </List>
        </Paper>

        <Button 
          variant="contained" 
          onClick={handleSaveNotifications}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          sx={{ bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark } }}
        >
          Save Preferences
        </Button>
      </CardContent>
    </Card>
  );

  const renderSecuritySection = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
          Security Settings
        </Typography>

        <Paper variant="outlined" sx={{ mb: 3 }}>
          <List disablePadding>
            <ListItem divider>
              <ListItemIcon><Security /></ListItemIcon>
              <ListItemText 
                primary="Two-Factor Authentication" 
                secondary="Add an extra layer of security to your account"
              />
              <Button variant="outlined" size="small" disabled>
                Coming Soon
              </Button>
            </ListItem>
            <ListItem divider>
              <ListItemIcon><Security /></ListItemIcon>
              <ListItemText 
                primary="Login History" 
                secondary="View your recent login activity"
              />
              <Button variant="outlined" size="small" disabled>
                Coming Soon
              </Button>
            </ListItem>
            <ListItem>
              <ListItemIcon><Warning color="error" /></ListItemIcon>
              <ListItemText 
                primary="Delete Account" 
                secondary="Permanently delete your account and all data"
              />
              <Button variant="outlined" color="error" size="small" disabled>
                Contact Support
              </Button>
            </ListItem>
          </List>
        </Paper>

        <Alert severity="info">
          For additional security options or to delete your account, please contact our support team.
        </Alert>
      </CardContent>
    </Card>
  );

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'profile':
        return renderProfileSection();
      case 'password':
        return renderPasswordSection();
      case 'notifications':
        return renderNotificationsSection();
      case 'security':
        return renderSecuritySection();
      default:
        return renderProfileSection();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
        Account Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your account settings and preferences
      </Typography>

      <Grid container spacing={3}>
        {/* Left sidebar menu */}
        <Grid item xs={12} md={3}>
          <Card>
            <List disablePadding>
              {menuItems.map((item, index) => (
                <ListItemButton
                  key={item.id}
                  selected={activeSection === item.id}
                  onClick={() => setActiveSection(item.id)}
                  sx={{
                    borderBottom: index < menuItems.length - 1 ? '1px solid #eee' : 'none',
                    '&.Mui-selected': {
                      bgcolor: `${brand.primary}1a`,
                      borderLeft: `3px solid ${brand.primary}`,
                      '&:hover': {
                        bgcolor: `${brand.primary}26`
                      }
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: activeSection === item.id ? brand.primary : 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: activeSection === item.id ? 600 : 400
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Card>
        </Grid>

        {/* Main content area */}
        <Grid item xs={12} md={9}>
          {renderActiveSection()}
        </Grid>
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AccountSettingsPage;
